import React,  { type DetailedHTMLProps, type HTMLAttributes } from 'react';
import type { IStyledComponent } from 'styled-components';
import type { FastOmit } from 'styled-components/dist/types';

import ErrorThrower from 'ErrorThrower';
import noop from 'functions/general/noop';
import onClickJustStopPropagation from 'functions/general/onClickJustStopPropagation';
import onClickPreventDefault from 'functions/general/onClickPreventDefault';
import useMovable from 'hooks/useMovable';
import {
  MainDiv,
  BackDropDiv,
  ModalSm,
  ModalMd,
  ModalContent,
  ModalHeaderDiv,
  ModalBodyDiv,
  ModalFooterDiv
} from './elements';

type StyleDivComponent = IStyledComponent<"web", FastOmit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, never>>;
export type ModalSize = 'md' | 'sm' | undefined;

export type Size = {
  width: string;
  height: string;
}

type ModalProps = {
  children: string | React.ReactNode;
  onClose(): void;
  size?: ModalSize;
}

type ModalGenericProps = {
  children: string | React.ReactNode;
  onClose(): void;
  ModalDiv: StyleDivComponent;
  id?: string;
  className?: string;
  movable?: boolean;
}

type ModalGeneralWithElemProps = {
  children: string | React.ReactNode;
  onClose(): void;
  ModalDiv: StyleDivComponent;
  classNameGeneral: string;
  id?: string;
  className?: string;
  movable?: boolean;
}

type ModalGeneralProps = {
  children: string | React.ReactNode;
  onClose(): void;
  classNameGeneral: string;
  id?: string;
  className?: string;
  movable?: boolean;
}

type ModalGenericWithSizeProps = ModalGenericProps & {
  size: Size;
}

type ModalHeaderProps = {
  children: string | React.ReactNode;
  onClose(): void;
}

type ModalBodyProps = {
  children: string | React.ReactNode;
}

type ModalFooterProps = {
  children: string | React.ReactNode;
}

const getModalBySize = (size: ModalSize) => {
  if (size === 'sm') return ModalSm;
  return ModalMd;
}

export const Modal = ({ children, onClose, size }: ModalProps) => {
  const Content = getModalBySize(size)
  return (
    <MainDiv>
      <Content>
        <ModalContent>
          {children}
        </ModalContent>
      </Content>
      <BackDropDiv onClick={onClose}/>
    </MainDiv>
  );
};

export const ModalGeneric = ({ children, onClose, ModalDiv, id, className="", movable=false }: ModalGenericProps) => {
  const props = id ? { id } : {};
  const {
    ref,
    onMouseLeave,
    onMouseMove,
    onMouseDown,
    onMouseUp
  } = useMovable(movable);

  return (
    <ErrorThrower componentName="ModalGeneric">
      <MainDiv>
        <ModalDiv
          {...props}
          className={className}
          ref={ref}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}>
          <ModalContent>
            {children}
          </ModalContent>
        </ModalDiv>
        <BackDropDiv onClick={onClose}/>
      </MainDiv>
    </ErrorThrower>
  );
};

export const ModalGeneralWithElem = ({
  children,
  onClose,
  id,
  ModalDiv,
  classNameGeneral,
  className="",
  movable=false
}: ModalGeneralWithElemProps) => {
  const props = id ? { id } : {};
  const {
    ref,
    onMouseLeave,
    onMouseMove,
    onMouseDown,
    onMouseUp
  } = useMovable(movable);

  return (
    <ErrorThrower componentName="ModalGeneral">
      <MainDiv>
        <ModalDiv
          {...props}
          className={className}
          ref={ref}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}>
          <ModalContent>
            <div
              onClick={onClickJustStopPropagation}
              role="button"
              onKeyDown={noop}
              tabIndex={0}
              className={classNameGeneral}>
              {children}
            </div>
          </ModalContent>
        </ModalDiv>
        <BackDropDiv onClick={onClose}/>
      </MainDiv>
    </ErrorThrower>
  );
};

export const ModalGeneral = ({
  children,
  onClose,
  id,
  classNameGeneral,
  className="",
  movable=false
}: ModalGeneralProps) => {
  const props = id ? { id } : {};
  const {
    ref,
    onMouseLeave,
    onMouseMove,
    onMouseDown,
    onMouseUp
  } = useMovable(movable);

  return (
    <ErrorThrower componentName="ModalGeneral">
      <MainDiv>
        <div
          {...props}
          className={className}
          ref={ref}
          role="presentation"
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}>
          <ModalContent>
            <div
              onClick={onClickJustStopPropagation}
              role="button"
              onKeyDown={noop}
              tabIndex={0}
              className={classNameGeneral}>
              {children}
            </div>
          </ModalContent>
        </div>
        <BackDropDiv onClick={onClose}/>
      </MainDiv>
    </ErrorThrower>
  );
};

export const ModalGenericWithSize = ({ children, onClose, ModalDiv, size }: ModalGenericWithSizeProps) => {
  return (
    <ErrorThrower componentName="ModalGeneric">
      <MainDiv>
        <ModalDiv {...size}>
          <ModalContent>
            {children}
          </ModalContent>
        </ModalDiv>
        <BackDropDiv onClick={onClose}/>
      </MainDiv>
    </ErrorThrower>
  );
};

export const ModalHeader = ({ children, onClose }: ModalHeaderProps) => (
  <ModalHeaderDiv onClick={onClickPreventDefault}>
    {children}
    <button type="button" aria-label="Close" onClick={onClose}>
      <span aria-hidden="true">×</span>
    </button>
  </ModalHeaderDiv>
);

export const ModalBody = ({ children }: ModalBodyProps) => (
  <ModalBodyDiv onClick={onClickPreventDefault}>
    {children}
  </ModalBodyDiv>
);

export const ModalFooter = ({ children }: ModalFooterProps) => (
  <ModalFooterDiv onClick={onClickPreventDefault}>
    {children}
  </ModalFooterDiv>
);
